import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";

/**
 * MagicLogin — iter220 (2026-03-02)
 *
 * Consumes a one-time magic-login token minted by the backend
 * (`/api/admin/customers/{id}/send-login-link` or the payment reminder
 * flow). Establishes the httpOnly session cookie, then forwards the
 * customer to `/dashboard?pay=<booking_id>` when a `pay` query param is
 * present so PaymentDueSection can auto-expand the invoice row.
 *
 * URL shape: `/auth/magic?token=<t>&pay=<booking_id>`
 */
export default function MagicLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  const token = params.get("token") || "";
  const payBookingId = params.get("pay") || "";
  // Guard against React.StrictMode double-invocation of useEffect in dev
  // (and any accidental effect re-runs) — the backend magic endpoint is
  // single-use, so a second call with the same token would 404.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("This sign-in link is missing its token.");
      return;
    }
    if (consumedRef.current) return;
    consumedRef.current = true;
    (async () => {
      try {
        await api.get(`/auth/magic?token=${encodeURIComponent(token)}`);
        // Populate AuthContext.user BEFORE navigating — otherwise the
        // Dashboard AuthGate sees the stale user=null (from the initial
        // /auth/me 401 fired before the cookie existed) and bounces to
        // /login before rendering the invoice row.
        try { await refreshAuth(); } catch { /* noop */ }
        setOk(true);
        // Short beat so the "signed in" affirmation is visible.
        setTimeout(() => {
          const dest = payBookingId
            ? `/dashboard?pay=${encodeURIComponent(payBookingId)}`
            : "/dashboard";
          navigate(dest, { replace: true });
        }, 450);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : "This sign-in link is invalid or has expired.";
        setError(msg);
      }
    })();
  }, [token, payBookingId, navigate, refreshAuth]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60"
      data-testid="magic-login-page"
    >
      <div className="w-full max-w-sm bg-white rounded-3xl border border-gray-200 shadow-xl p-6 sm:p-7">
        {!error && !ok && (
          <>
            <div className="flex items-center gap-3">
              <Loader2 className="w-6 h-6 text-emerald-700 animate-spin" />
              <div>
                <div className="text-lg font-extrabold text-gray-900 font-[Outfit] tracking-tight">
                  Signing you in…
                </div>
                <div className="text-sm text-gray-600 mt-0.5">
                  Bringing you to your JDSN account.
                </div>
              </div>
            </div>
          </>
        )}
        {ok && (
          <div className="flex items-center gap-3" data-testid="magic-login-success">
            <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            <div>
              <div className="text-lg font-extrabold text-gray-900 font-[Outfit] tracking-tight">
                Signed in ✓
              </div>
              <div className="text-sm text-gray-600 mt-0.5">
                Opening your invoice…
              </div>
            </div>
          </div>
        )}
        {error && (
          <div data-testid="magic-login-error">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-extrabold text-gray-900 font-[Outfit] tracking-tight">
                  Link no longer valid
                </div>
                <div className="text-sm text-gray-600 mt-0.5">{error}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-4 w-full h-11 rounded-full bg-emerald-700 text-white font-extrabold text-sm hover:bg-emerald-800"
              data-testid="magic-login-fallback-btn"
            >
              Sign in with your password
            </button>
            <p className="mt-3 text-xs text-gray-500">
              If you keep seeing this message, ask JDSN Lawn Care to resend a
              fresh reminder.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
